/**
 * @file boardroomsRange.ts
 * @brief Boardrooms' date range: all time unless the user picks one (W37).
 *
 * @details
 * W37 (DES 04-11-2026): Boardrooms started on "This month", so a meeting
 * from an earlier month (one shared with me on 4 Nov but dated 2 Oct) was
 * hidden and the page looked empty, the App Store review account included.
 * Now the list starts on all time, newest first, like the phone and Home.
 * - A range the user picks is remembered on this device, per user, the way
 *   Mine | Team is (`meetingListScope.ts`: localStorage, storage blocked =
 *   this page load only). Nothing is stored until a pick; Clear forgets it.
 * - A preset is stored by name, so "This month" is always the current month.
 *   A custom range is stored with its two dates.
 * - An empty picked range with meetings at other times says so, with one
 *   button "Show all meetings" (`emptyRangeHasOthers`).
 *
 * Pure (no `@/` imports) so `node --test` can load it.
 *
 * @author DES
 * @date 04-11-2026
 */

export type StoredPreset = "today" | "yesterday" | "week" | "month" | "lastMonth";
export type StoredRange =
  | { preset: StoredPreset }
  | { preset: "custom"; from: Date; to: Date };

const PRESETS: readonly StoredPreset[] = ["today", "yesterday", "week", "month", "lastMonth"];

/** One remembered range per user on a shared device. */
export function rangeStorageKey(userId: string | null | undefined): string | null {
  return userId ? `verblizr.boardroomsRange.${userId}` : null;
}

export function serializeRange(r: StoredRange): string {
  return r.preset === "custom"
    ? JSON.stringify({ preset: "custom", from: r.from.toISOString(), to: r.to.toISOString() })
    : JSON.stringify({ preset: r.preset });
}

/** Anything unreadable is "no range" (all time), never an error. */
export function parseStoredRange(raw: string | null | undefined): StoredRange | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== "object") return null;
  const o = v as { preset?: unknown; from?: unknown; to?: unknown };
  if (typeof o.preset !== "string") return null;
  if ((PRESETS as readonly string[]).includes(o.preset)) return { preset: o.preset as StoredPreset };
  if (o.preset !== "custom" || typeof o.from !== "string" || typeof o.to !== "string") return null;
  const from = new Date(o.from);
  const to = new Date(o.to);
  if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || from > to) return null;
  return { preset: "custom", from, to };
}

export function readStoredRange(key: string | null): StoredRange | null {
  if (!key) return null;
  try {
    return parseStoredRange(window.localStorage.getItem(key));
  } catch {
    return null;
  }
}

/** null forgets the pick (back to all time). */
export function writeStoredRange(key: string | null, r: StoredRange | null): void {
  if (!key) return;
  try {
    if (r) window.localStorage.setItem(key, serializeRange(r));
    else window.localStorage.removeItem(key);
  } catch {
    // Storage blocked: the pick lasts for this page load.
  }
}

/**
 * The "Show all meetings" empty state: a picked range came back empty, no
 * search or tag narrows it (a tag already ignores the date), and the same
 * list without the date has meetings.
 */
export function emptyRangeHasOthers(f: {
  rangePicked: boolean;
  shown: number;
  search: string;
  tags: number;
  totalWithoutRange: number | null | undefined;
}): boolean {
  return (
    f.rangePicked &&
    f.shown === 0 &&
    !f.search &&
    f.tags === 0 &&
    typeof f.totalWithoutRange === "number" &&
    f.totalWithoutRange > 0
  );
}

/** "You have 3 meetings at other times." */
export function otherMeetingsText(total: number): string {
  return total === 1
    ? "You have 1 meeting at another time."
    : `You have ${total} meetings at other times.`;
}
